/**
 * deploymentWording.utils — how a device is spoken about in anything an
 * operator reads: our own strings and whatever a route hands back.
 *
 * Mike, 2026-09-01: "no deployment will have real + demo devices, strictly
 * real only or demo only" and "any devices that have sim in the name, call
 * them demo devices". Two rules follow:
 *
 *   1. A message explaining a REAL vs SIMULATED split describes a state the
 *      deployment reading it cannot be in, so it is dropped and the caller's
 *      own wording is shown instead.
 *   2. Everything else that says "simulated" says DEMO.
 */

const DEMO_TERMS = /\b(?:simulat\w*|demo)\b/i;
const REAL_TERMS = /\breal\b|\bmix(?:ed|es|ing)?\b|\bnon-?simulated\b/i;

/** True when the text turns on a real/demo split — a distinction no deployment has. */
export const isRealVersusDemoMessage = (text) =>
  typeof text === 'string' && DEMO_TERMS.test(text) && REAL_TERMS.test(text);

const matchCase = (sample, word) =>
  sample[0] === sample[0]?.toUpperCase() ? `${word[0].toUpperCase()}${word.slice(1)}` : word;

/**
 * "this simulated device" → "this demo device". Only the words that describe a
 * device change; "simulator" and the rest of the vocabulary are left alone.
 */
export const toDemoWording = (text) =>
  typeof text === 'string'
    ? text
        .replace(/\bsimulated\b/gi, (match) => matchCase(match, 'demo'))
        .replace(/\bsimulation(?=\s+(?:device|devices|group|groups|node|nodes)\b)/gi, (match) =>
          matchCase(match, 'demo'),
        )
    : text;

/**
 * What an operator should be shown for `text`: nothing when it only explains a
 * real/demo split, otherwise the same message in demo wording.
 */
export const applyDeploymentWording = (text) =>
  !text || isRealVersusDemoMessage(text) ? '' : toDemoWording(text);
